<template>
  <div class="overflow-hidden">
    <div
      :class="isExpanded ? 'pb-3' : 'line-clamp-4 overflow-ellipsis'"
    >
      <slot />
    </div>
    <button
      v-if="shouldShowButton"
      class="mt-2 text-blue-500 hover:text-blue-600 text-xs underline"
      @click="toggleExpand"
    >
      {{ isExpanded ? collapseLabel : expandLabel }}
    </button>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  maxLines: {
    type: Number,
    default: 4,
  },
  lineHeight: {
    type: Number,
    default: 1.5, // line-height в rem
  },
  fontSize: {
    type: Number,
    default: 0.875, // text-sm = 14px = 0.875rem
  },
  expandLabel: {
    type: String,
    default: 'Читать далее',
  },
  collapseLabel: {
    type: String,
    default: 'Свернуть',
  },
})

const isExpanded = ref(false)

// Функция для подсчета примерного количества строк на основе текста
const estimateLines = (text) => {
  if (!text) return 0

  // Убираем лишние пробелы, но сохраняем переносы строк
  const cleanText = text?.trim?.()
  if (!cleanText) return 0
  // Разбиваем на строки по переносам
  const lines = cleanText.split('\n')

  let totalLines = 0

  lines.forEach(line => {
    if (!line.trim()) {
      totalLines += 1 // пустая строка
      return
    }

    // Более точный расчет ширины символов
    // Учитываем, что разные символы имеют разную ширину
    const avgCharWidth = getAverageCharWidth(line)

    // Примерная ширина контейнера (учитываем отступы)
    const containerWidth = 360 // px с учетом padding

    // Количество символов в строке
    const charsPerLine = Math.floor(containerWidth / avgCharWidth)

    // Подсчитываем количество строк для этой строки
    const lineCount = Math.ceil(line.length / charsPerLine)
    totalLines += lineCount
  })

  return totalLines
}

// Функция для расчета средней ширины символов в строке
const getAverageCharWidth = (text) => {
  if (!text) return 8

  // Базовые размеры для text-sm (14px)
  const baseFontSize = props.fontSize * 16 // конвертируем rem в px

  // Коэффициенты для разных типов символов
  const charWidths = {
    // Кириллица и латиница
    'a-z': baseFontSize * 0.6,
    'а-я': baseFontSize * 0.65,
    // Цифры
    '0-9': baseFontSize * 0.55,
    // Пунктуация
    '.,;:!?': baseFontSize * 0.3,
    // Пробелы
    ' ': baseFontSize * 0.3,
    // Широкие символы
    'WMWQ': baseFontSize * 0.8,
    // Узкие символы
    'il|': baseFontSize * 0.3,
  }

  let totalWidth = 0
  let charCount = 0

  for (const char of text) {
    let charWidth = baseFontSize * 0.6 // базовый размер

    if (/[a-z]/.test(char)) {
      charWidth = charWidths['a-z']
    } else if (/[а-яё]/i.test(char)) {
      charWidth = charWidths['а-я']
    } else if (/[0-9]/.test(char)) {
      charWidth = charWidths['0-9']
    } else if (/[.,;:!?]/.test(char)) {
      charWidth = charWidths['.,;:!?']
    } else if (char === ' ') {
      charWidth = charWidths[' ']
    } else if (/[WMWQ]/.test(char)) {
      charWidth = charWidths['WMWQ']
    } else if (/[il|]/.test(char)) {
      charWidth = charWidths['il|']
    }

    totalWidth += charWidth
    charCount++
  }

  return charCount > 0 ? totalWidth / charCount : baseFontSize * 0.6
}

// Вычисляем, нужно ли показывать кнопку
const shouldShowButton = computed(() => {
  if (!props.text) return false

  const estimatedLines = estimateLines(props.text)
  return estimatedLines > props.maxLines
})

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
}
</script>
